import { useApolloClient } from '@frameio/graphql-client';
import { ResourcesGrantingAccess } from '@frameio/graphql-client/generated';
import { DEFAULT_INITIAL_PAGE_SIZE } from '@frameio/utils';

export const useResourcesGrantingAccess = (
  resourceId: string,
  resourceTypename: string
) => {
  const client = useApolloClient();

  /**
   * Reads and filters resources from Apollo cache that grant access to the current resource
   * Applies filtering rules:
   * - Excludes ACCOUNT type resources for Projects
   * - Excludes the current resource from its own list
   * @returns Filtered array of resources that grant access
   */
  const getFilteredResources = () => {
    const accessibleResource = client.readFragment({
      id: client.cache.identify({
        __typename: resourceTypename,
        id: resourceId,
      }),
      fragment: ResourcesGrantingAccess,
      fragmentName: 'ResourcesGrantingAccess',
      variables: { page: { first: DEFAULT_INITIAL_PAGE_SIZE } },
    });

    /**
     * @note Make sure that the current resource being viewed is excluded from the render
     */
    return accessibleResource?.accessGrantingResources?.nodes?.filter(
      ({ id, resourceType }) => {
        if (resourceType === 'ACCOUNT') {
          return resourceTypename !== 'Project';
        }
        return id !== resourceId;
      }
    );
  };

  /**
   * Conditionally refetches resources data when needed
   * Used when toggling project privacy to ensure we have complete data
   */
  const refetchResources = () => {
    const filteredResources = getFilteredResources();

    // Only refetch if we don't have any filtered resources in cache
    if (!filteredResources?.length) {
      client.refetchQueries({
        include: ['GetResourcesGrantingAccess'],
        updateCache: (cache) => {
          cache.evict({
            fieldName: 'accessGrantingResources',
            id: cache.identify({
              __typename: resourceTypename,
              id: resourceId,
            }),
          });
        },
      });
    }
  };

  return {
    resourcesGrantingAccess: getFilteredResources(),
    refetchResources,
  };
};
